/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useForm, Controller } from 'react-hook-form';
import CreatableSelect from 'react-select/creatable';
import Select from 'react-select';
import { FaPlay, FaTrash, FaArrowRight, FaArrowLeft, FaCheck, FaFileUpload, FaUserTie, FaVolumeUp, FaStop, FaSpinner } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    difficultyOptions,
    durationOptions,
    FormInputs,
    jobRoleOptions,
    Option,
    sessionTypeOptions,
    topicOptionsByJobRole,
} from '../../../types/session-forms.types';
import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firebase-firestore';
import 'firebase/storage';
import { Collections } from '../../../types/collection';
import api from '../../../libs/axiosInstance';
import { useUserContext } from '../../../contexts/user.context';
import { Toast } from 'primereact/toast';
import { interviewTypes } from '../../../pages/NewSessionTypeSelectionPage';
import { useDropzone } from 'react-dropzone';
import { captureException } from '@sentry/react';
import { Subscriptions } from '../../payments/enhanced-pricing-plans.type';
// import { useFeatureAccess } from '../../../hooks/useFeatureAccess'; // Removed - using real-time validation instead
import { apiUsageService } from '../../../services/api-usage.service';
import { trialService } from '../../../services/trial.service';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { environmentPricingService } from '../../../services/environment-pricing.service';

interface Props {
    icon: React.ReactElement;
    type: string;
}

export const getInterViewLabel = (type: string) => {
    return interviewTypes.find((it) => it.value === type)?.name;
};

// Add interviewer data
// Interviewer profiles with corresponding ElevenLabs voice IDs
interface InterviewerProfile {
    id: string;
    name: string;
    title: string;
    description: string;
    image: string;
    voiceId: string;
    gender: 'male' | 'female';
}

const INTERVIEWER_PROFILES: InterviewerProfile[] = [
    {
        id: 'sarah',
        name: 'Sarah Chen',
        title: 'Senior Recruiter',
        description: 'Friendly and supportive with a focus on bringing out your best qualities.',
        image: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?q=80&w=300&h=300&auto=format&fit=crop',
        voiceId: '21m00Tcm4TlvDq8ikWAM', // Rachel voice
        gender: 'female'
    },
    {
        id: 'michael',
        name: 'Michael Rodriguez',
        title: 'Engineering Manager',
        description: 'Detailed and technical with an eye for problem-solving skills.',
        image: 'https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=300&h=300&auto=format&fit=crop',
        voiceId: 'pNInz6obpgDQGcFmaJgB', // Adam voice
        gender: 'male'
    },
    {
        id: 'alex',
        name: 'Alex Thompson',
        title: 'Startup Founder',
        description: 'Direct and fast-paced with a focus on cultural fit and adaptability.',
        image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=300&h=300&auto=format&fit=crop',
        voiceId: 'ErXwobaYiN019PkySvjV', // Antoni voice
        gender: 'male'
    },
    {
        id: 'jessica',
        name: 'Jessica Kim',
        title: 'HR Director',
        description: 'Empathetic and thorough with focus on soft skills and experience.',
        image: 'https://images.unsplash.com/photo-1580489944761-15a19d654956?q=80&w=300&h=300&auto=format&fit=crop',
        voiceId: 'EXAVITQu4vr4xnSDxMaL', // Bella voice
        gender: 'female'
    },
    {
        id: 'david',
        name: 'David Washington',
        title: 'Product Manager',
        description: 'Analytical and strategic with focus on practical examples and results.',
        image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=300&h=300&auto=format&fit=crop',
        voiceId: 'yoZ06aMxZJJ28mfd3POQ', // Sam voice
        gender: 'male'
    }
];

// Add selectedInterviewerId to FormInputs
// This is a custom extension that will be merged with the imported type
interface ExtendedFormInputs extends FormInputs {
    selectedInterviewerId?: string;
}

// First, add a helper function to check for premium plans
const isPremiumPlan = (user: any) => {
    return user?.subscription?.name === Subscriptions.pro || user?.subscription?.name === Subscriptions.enterprise;
};

const NewInterviewSessionForm = ({ icon }: Props) => {
    const [searchParams] = useSearchParams();
    const type = searchParams.get('type');
    const toast = useRef<Toast>(null);
    const { user, reloadUserData } = useUserContext();
    const navigate = useNavigate();
    // const featureAccess = useFeatureAccess(); // Removed - using real-time validation instead

    const [currentStep, setCurrentStep] = useState(1);
    const [isReviewing, setIsReviewing] = useState(false);
    const [interviewFormData, setInterviewFormData] = useState<ExtendedFormInputs>();
    const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedInterviewer, setSelectedInterviewer] = useState<InterviewerProfile | null>(null);
    const [playingPreview, setPlayingPreview] = useState<string | null>(null);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const [userHistory, setUserHistory] = useState<{ jobRole?: string; duration?: number; difficulty?: string } | null>(null);
    const [showExitConfirm, setShowExitConfirm] = useState(false);

    const {
        handleSubmit,
        control,
        watch,
        register,
        formState: { errors, touchedFields },
        trigger,
        setValue,
    } = useForm<ExtendedFormInputs>({
        defaultValues: {
            sessionType: null,
            title: '',
            jobRole: null,
            difficulty: null,
            duration: null,
            topics: [],
            selectedInterviewerId: '',
        },
        mode: 'onBlur', // Validate on blur for better UX
    });

    useEffect(() => {
        // Reload user data to ensure subscription info is up-to-date
        const loadData = async () => {
            setIsInitialLoading(true);
            try {
                await reloadUserData();

                // Fetch user session history for smart defaults
                const userId = firebase.auth().currentUser?.uid;
                if (userId) {
                    try {
                        const sessionsSnapshot = await firebase
                            .firestore()
                            .collection(Collections.Sessions)
                            .doc(userId)
                            .collection('list')
                            .orderBy('createdAt', 'desc')
                            .limit(5)
                            .get();

                        if (!sessionsSnapshot.empty) {
                            // Find most common job role, duration, difficulty from recent sessions
                            const recentSessions = sessionsSnapshot.docs.map(doc => doc.data() as ExtendedFormInputs);
                            const jobRoleCounts: { [key: string]: number } = {};
                            const durationCounts: { [key: string]: number } = {};
                            const difficultyCounts: { [key: string]: number } = {};

                            recentSessions.forEach(session => {
                                if (session.jobRole?.value) {
                                    jobRoleCounts[session.jobRole.value] = (jobRoleCounts[session.jobRole.value] || 0) + 1;
                                }
                                if (session.duration?.value) {
                                    durationCounts[String(session.duration.value)] = (durationCounts[String(session.duration.value)] || 0) + 1;
                                }
                                if (session.difficulty?.value) {
                                    difficultyCounts[session.difficulty.value] = (difficultyCounts[session.difficulty.value] || 0) + 1;
                                }
                            });

                            const mostCommonJobRole = Object.keys(jobRoleCounts).length > 0
                                ? Object.keys(jobRoleCounts).reduce((a, b) =>
                                    jobRoleCounts[a] > jobRoleCounts[b] ? a : b)
                                : undefined;
                            const mostCommonDuration = Object.keys(durationCounts).length > 0
                                ? Object.keys(durationCounts).reduce((a, b) =>
                                    durationCounts[a] > durationCounts[b] ? a : b)
                                : undefined;
                            const mostCommonDifficulty = Object.keys(difficultyCounts).length > 0
                                ? Object.keys(difficultyCounts).reduce((a, b) =>
                                    difficultyCounts[a] > difficultyCounts[b] ? a : b)
                                : undefined;

                            if (mostCommonJobRole || mostCommonDuration || mostCommonDifficulty) {
                                setUserHistory({
                                    jobRole: mostCommonJobRole,
                                    duration: mostCommonDuration ? parseInt(mostCommonDuration) : undefined,
                                    difficulty: mostCommonDifficulty,
                                });
                            }
                        }
                    } catch (error) {
                        // Silently fail - smart defaults are optional
                        console.debug('Could not load user history for smart defaults:', error);
                    }
                }
            } finally {
                setIsInitialLoading(false);
            }
        };
        loadData();
    }, []);

    // Auto-select first interviewer for non-premium users
    useEffect(() => {
        if (!isPremiumPlan(user) && INTERVIEWER_PROFILES.length > 0) {
            setSelectedInterviewer(INTERVIEWER_PROFILES[0]);
        }
    }, [user]);

    // Apply smart defaults from user history
    useEffect(() => {
        if (userHistory && !touchedFields.jobRole && !watch('jobRole')) {
            const suggestedJobRole = jobRoleOptions.find(opt => opt.value === userHistory.jobRole);
            if (suggestedJobRole) {
                setValue('jobRole', suggestedJobRole, { shouldTouch: false });
            }
        }

        if (userHistory?.duration && !touchedFields.duration && !watch('duration')) {
            // Only apply suggested duration if it's valid for the current plan
            const planName = user?.subscription?.name;
            const currentPlan = planName ? environmentPricingService.getPlanByName(planName) : null;
            const allowedDurations = currentPlan?.interviewLengths || [];

            // Check if the suggested duration is allowed in the current plan
            if (allowedDurations.includes(String(userHistory.duration))) {
                const suggestedDuration = {
                    value: String(userHistory.duration),
                    label: `${userHistory.duration} Minutes`
                };
                setValue('duration', suggestedDuration, { shouldTouch: false });
            }
        }

        if (userHistory?.difficulty && !touchedFields.difficulty && !watch('difficulty')) {
            const suggestedDifficulty = difficultyOptions.find(opt => opt.value === userHistory.difficulty);
            if (suggestedDifficulty) {
                setValue('difficulty', suggestedDifficulty, { shouldTouch: false });
            }
        }
    }, [userHistory, setValue, watch, touchedFields]);

    const onDropAccepted = useCallback(
        (acceptedFiles: File[]) => {
            // Check if user's plan allows file uploading
            if (!user?.subscription.fileUpload) {
                toast.current?.show({
                    severity: 'warn',
                    summary: '📎 File Upload Not Available',
                    detail: 'File uploads are available on Pro and Enterprise plans. Upgrade to upload documents and get personalized feedback!',
                    life: 4000,
                });
                return;
            }

            setSelectedFiles((prev) => [...prev, ...acceptedFiles]);
        },
        [user]
    );

    const onDropRejected = useCallback(
        (fileRejections: any) => {
            fileRejections.forEach(({ file, errors }: { file: any; errors: any }) => {
                errors.forEach((err: any) => {
                    if (err.code === 'file-too-large') {
                        toast.current?.show({
                            severity: 'warn',
                            summary: '📁 File Too Large',
                            detail: `${file.name} exceeds the 2MB limit. Please compress your file or choose a smaller one.`,
                            life: 4000,
                        });
                    } else {
                        toast.current?.show({
                            severity: 'warn',
                            summary: 'File Rejected',
                            detail: `${file.name} could not be uploaded. ${err.message}`,
                            life: 3000,
                        });
                    }
                });
            });
        },
        []
    );

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDropAccepted,
        onDropRejected,
        multiple: false,
        accept: {
            'application/pdf': ['.pdf'],
            'application/msword': ['.doc', '.docx'],
        },
        maxSize: 2 * 1024 * 1024,
    });

    const removeFile = (fileIndex: number) => {
        setSelectedFiles((prev) => prev.filter((_, idx) => idx !== fileIndex));
    };

    const populateDurationOption = () => {
        // Get the current plan's interviewLengths from the pricing service
        // This ensures we always use the latest plan definition, not stored user data
        const planName = user?.subscription?.name;
        if (planName) {
            const currentPlan = environmentPricingService.getPlanByName(planName);
            if (currentPlan?.interviewLengths) {
                return currentPlan.interviewLengths.map((interviewLength: string) => ({
                    value: interviewLength,
                    label: `${interviewLength} Minutes`,
                }));
            }
        }
        return durationOptions;
    };

    // Function to play interviewer voice preview
    const playVoicePreview = async (interviewerId: string) => {
        try {
            // If already playing something, stop it
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
                setPlayingPreview(null);
            }

            // Don't proceed if we're trying to stop the current preview
            if (playingPreview === interviewerId) {
                return;
            }

            // Set as currently playing
            setPlayingPreview(interviewerId);

            // Find the interviewer
            const interviewer = INTERVIEWER_PROFILES.find(i => i.id === interviewerId);
            if (!interviewer) return;

            // Create preview text - could be customized per interviewer
            const previewText = `Hi, I'm ${interviewer.name}. I'll be your interviewer today.`;

            // Get the audio from ElevenLabs
            const response = await fetch(`https://api.elevenlabs.io/v1/text-to-speech/${interviewer.voiceId}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'xi-api-key': import.meta.env.VITE_ELEVEN_LABS,
                },
                body: JSON.stringify({
                    text: previewText,
                    model_id: 'eleven_multilingual_v2',
                    voice_settings: {
                        stability: 0.5,
                        similarity_boost: 0.75,
                        use_speaker_boost: true
                    }
                })
            });

            if (!response.ok) {
                throw new Error('Failed to fetch voice preview');
            }

            const audioBlob = await response.blob();
            const audioUrl = URL.createObjectURL(audioBlob);

            // Play the audio
            const audio = new Audio(audioUrl);
            audioRef.current = audio;

            audio.onended = () => {
                setPlayingPreview(null);
                audioRef.current = null;
            };

            await audio.play();
        } catch (error) {
            console.error('Error playing voice preview:', error);
            setPlayingPreview(null);
            toast.current?.show({
                severity: 'error',
                summary: 'Preview Error',
                detail: 'Could not play voice preview.',
                life: 3000,
            });
        }
    };

    // Stop any playing audio when component unmounts
    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current = null;
            }
        };
    }, []);

    const onSubmit = async (data: ExtendedFormInputs) => {
        // Ensure interviewer is selected
        if (!selectedInterviewer) {
            toast.current?.show({
                severity: 'warn',
                summary: '👤 Interviewer Required',
                detail: 'Please select an interviewer to personalize your practice session experience.',
                life: 3000,
            });
            return;
        }

        setInterviewFormData({
            ...data,
            selectedInterviewerId: selectedInterviewer.id
        });
        setIsReviewing(true);
    };

    const submitData = async (data: ExtendedFormInputs) => {
        try {
            setIsSubmitting(true);
            const userId = firebase.auth().currentUser?.uid;
            if (!userId || !type) return;

            // REAL-TIME VALIDATION: Always check fresh usage before creating session
            try {
                const realTimeCheck = await apiUsageService.canCreateInterviewSession();
                if (!realTimeCheck.allowed) {
                    toast?.current?.show({
                        severity: 'warn',
                        summary: '🎯 Session Limit Reached',
                        detail: realTimeCheck.message || `You've used ${realTimeCheck.used} of ${realTimeCheck.limit} interview sessions this month. Upgrade your plan to continue improving your interview skills!`,
                        life: 5000,
                    });
                    setIsSubmitting(false);
                    return;
                }
            } catch (error) {
                console.error('Error checking session limits:', error);
                toast?.current?.show({
                    severity: 'error',
                    summary: '❌ Validation Failed',
                    detail: 'Unable to verify your session limits. Please try again or contact support.',
                    life: 5000,
                });
                setIsSubmitting(false);
                return;
            }

            if (selectedFiles.length > 0) {
                toast?.current?.show({
                    severity: 'info',
                    summary: 'Please wait...',
                    detail: 'Uploading files',
                    life: 5000,
                });
            }

            // Create session list reference
            const sessionListRef = firebase
                .firestore()
                .collection(Collections.Sessions)
                .doc(userId)
                .collection('list');

            // Create a new Session doc ID
            const newSessionId = firebase
                .firestore()
                .collection(Collections.Sessions)
                .doc().id;

            // Upload files to Firebase
            const storageRef = firebase.storage().ref();
            const fileUrls: string[] = [];

            for (const file of selectedFiles) {
                const fileRef = storageRef.child(
                    `user-docs/${userId}/${Date.now()}-${file.name}`
                );
                await fileRef.put(file);
                const downloadURL = await fileRef.getDownloadURL();
                fileUrls.push(downloadURL);
            }

            // Find the selected interviewer's complete data
            const interviewerData = selectedInterviewer ||
                INTERVIEWER_PROFILES.find(i => i.id === data.selectedInterviewerId) ||
                INTERVIEWER_PROFILES[0]; // Default to first interviewer as fallback

            // Create session doc with interviewer data
            await sessionListRef.doc(newSessionId).set({
                ...data,
                generatedQuestion: false,
                generatingQuestion: true,
                // State machine fields for idempotency
                generationState: 'pending',
                generationAttempts: 0,
                appActionText: 'Working off information provided',
                completed: false,
                interviewType: type,
                status: 'In Progress',
                score: 0,
                duration: data.duration,
                timeRemaining: parseInt(`${data.duration?.value || 0}`) * 60,
                createdAt: new Date().toISOString(),
                fileSources: fileUrls,
                interviewer: {
                    id: interviewerData.id,
                    name: interviewerData.name,
                    title: interviewerData.title,
                    image: interviewerData.image,
                    voiceId: interviewerData.voiceId
                }
            });

            // Usage is already incremented by the backend in /ai/generate-questions endpoint
            // Just emit event to refresh usage dashboard
            window.dispatchEvent(new CustomEvent('session-created'));

            // Track trial usage if user is in trial
            await trialService.trackUsage({
                action: 'session_created',
                metadata: {
                    sessionType: data.sessionType?.label,
                    feature: 'interview_practice'
                }
            });

            // Kick off question generation
            setTimeout(async () => {
                try {
                    await api.post('/ai/generate-questions', {
                        sessionType: data.sessionType?.label,
                        jobRole: data.jobRole?.label,
                        difficulty: data.difficulty?.label,
                        duration: data.duration?.value,
                        topics: data.topics?.map((t) => t.label),
                        timeRemaining: parseInt(`${data.duration?.value || 0}`) * 60,
                        userId,
                        sessionId: newSessionId,
                        interviewType: type,
                        sessionTitle: data.title,
                        fileSources: fileUrls,
                        interviewerId: interviewerData.id,
                        interviewerVoiceId: interviewerData.voiceId
                    });
                    // Emit event to refresh usage dashboard after successful generation request
                    window.dispatchEvent(new CustomEvent('session-created'));
                } catch (error) {
                    console.error('Error generating questions:', error);
                    // Still emit event even if generation fails (usage was already incremented)
                    window.dispatchEvent(new CustomEvent('session-created'));
                }
            }, 500);


            navigate(`/session-details/${newSessionId}`);
        } catch (error) {
            captureException(error);
            toast?.current?.show({
                severity: 'error',
                summary: '❌ Session Creation Failed',
                detail: 'Could not create your interview session. Please check your connection and try again.',
                life: 4000,
            });
            setIsSubmitting(false);
        }
    };

    const selectedJobRole = watch('jobRole');
    const topicOptions: Option[] = selectedJobRole
        ? topicOptionsByJobRole[selectedJobRole.value] || []
        : [];

    // Function to handle selectedInterviewer null check
    const validateSelectedInterviewer = () => {
        if (!selectedInterviewer) {
            toast.current?.show({
                severity: 'warn',
                summary: 'Interviewer Required',
                detail: 'Please select an interviewer to continue.',
                life: 3000,
            });
            return false;
        }
        return true;
    };

    // Update validateStep function to skip interviewer validation for non-premium users
    const validateStep = async (step: number) => {
        let isValid = true;

        switch (step) {
            case 1:
                isValid = await trigger(['title', 'sessionType']);
                break;
            case 2:
                isValid = await trigger(['jobRole', 'difficulty']);
                break;
            case 3:
                isValid = await trigger(['duration', 'topics']);
                break;
            case 4:
                // Skip validation for non-premium users as interviewer is pre-selected
                if (isPremiumPlan(user)) {
                    isValid = validateSelectedInterviewer();
                }
                if (isValid && selectedInterviewer) {
                    // Set the selected interviewer ID in the form
                    setValue('selectedInterviewerId', selectedInterviewer.id);
                }
                break;
            default:
                break;
        }

        return isValid;
    };

    // Update nextStep and prevStep for the new step
    const nextStep = async () => {
        const isValid = await validateStep(currentStep);
        if (isValid) {
            setCurrentStep((prev) => Math.min(prev + 1, 5));
        }
    };

    const prevStep = () => {
        setCurrentStep((prev) => Math.max(prev - 1, 1));
    };

    // Update the renderStepIndicator to include the new step
    const renderStepIndicator = () => {
        const totalSteps = 5;
        const steps = [
            { number: 1, label: 'Basic Info' },
            { number: 2, label: 'Role & Level' },
            { number: 3, label: 'Duration & Topics' },
            { number: 4, label: 'Interviewer' },
            { number: 5, label: 'Review' },
        ];

        return (
            <div className="mb-6 sm:mb-8">
                {/* Progress bar */}
                <div className="mb-4 sm:mb-6">
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                            className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300 ease-out"
                            style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                        />
                    </div>
                </div>

                {/* Step indicators */}
                <div className="flex justify-between relative px-2 sm:px-0">
                    {/* Connecting line */}
                    <div className="absolute top-5 left-8 right-8 h-0.5 bg-gray-200 -z-10 hidden sm:block" />
                    <div
                        className="absolute top-5 left-8 h-0.5 bg-gradient-to-r from-green-500 to-blue-500 transition-all duration-300 ease-out -z-10 hidden sm:block"
                        style={{ width: `${((currentStep - 1) / (totalSteps - 1)) * 100}%` }}
                    />

                    {steps.map((step) => {
                        const isCompleted = step.number < currentStep;
                        const isActive = step.number === currentStep;

                        return (
                            <div key={step.number} className="flex flex-col items-center flex-1 max-w-[20%]">
                                <div
                                    className={`relative w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center text-xs sm:text-sm font-semibold transition-all duration-300 ${isCompleted
                                        ? 'bg-green-500 text-white shadow-lg scale-110'
                                        : isActive
                                            ? 'bg-blue-500 text-white shadow-lg ring-4 ring-blue-200 scale-110'
                                            : 'bg-gray-200 text-gray-500'
                                        }`}
                                >
                                    {isCompleted ? <FaCheck className="text-xs sm:text-sm" /> : step.number}
                                </div>
                                <span
                                    className={`text-[10px] sm:text-xs mt-1.5 sm:mt-2 text-center px-0.5 font-medium leading-tight ${isActive ? 'text-blue-600' : isCompleted ? 'text-green-600' : 'text-gray-500'
                                        }`}
                                >
                                    <span className="hidden sm:inline">{step.label}</span>
                                    <span className="sm:hidden">{step.number}</span>
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    if (isReviewing && interviewFormData) {
        // Find the selected interviewer data
        const interviewerData = selectedInterviewer ||
            INTERVIEWER_PROFILES.find(i => i.id === interviewFormData.selectedInterviewerId);

        return (
            <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8 bg-gray-50">
                <Toast ref={toast} />
                <motion.div
                    className="w-full max-w-4xl mx-auto"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-4 sm:mb-6 text-center text-gray-800">Review Your Session</h2>

                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
                        {/* Left column: Interviewer details */}
                        {interviewerData && (
                            <motion.div
                                className="lg:col-span-1 bg-white rounded-xl shadow-lg overflow-hidden"
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.5, delay: 0.1 }}
                            >
                                <div className="relative">
                                    <div className="h-32 sm:h-40 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
                                    <div className="absolute top-16 sm:top-24 left-1/2 transform -translate-x-1/2">
                                        <img
                                            src={interviewerData.image}
                                            alt={interviewerData.name}
                                            className="w-24 h-24 sm:w-32 sm:h-32 rounded-full border-4 border-white object-cover shadow-md"
                                        />
                                    </div>
                                </div>

                                <div className="text-center pt-12 sm:pt-16 p-4 sm:p-6">
                                    <h3 className="text-xl sm:text-2xl font-bold text-gray-800">{interviewerData.name}</h3>
                                    <p className="text-gray-600 text-base sm:text-lg mb-2">{interviewerData.title}</p>
                                    <div className="flex justify-center my-3 sm:my-4">
                                        <span className={`px-3 py-1 rounded-full text-xs sm:text-sm font-medium ${interviewerData.gender === 'female' ? 'bg-pink-100 text-pink-800' : 'bg-blue-100 text-blue-800'}`}>
                                            {interviewerData.gender === 'female' ? 'Female Interviewer' : 'Male Interviewer'}
                                        </span>
                                    </div>
                                    <p className="text-sm sm:text-base text-gray-600 mb-4 sm:mb-6 italic">"{interviewerData.description}"</p>

                                    <motion.button
                                        type="button"
                                        onClick={() => playVoicePreview(interviewerData.id)}
                                        className="flex items-center justify-center space-x-2 mx-auto px-4 py-2 bg-indigo-100 text-indigo-700 rounded-full hover:bg-indigo-200 transition-colors text-sm sm:text-base min-h-[44px]"
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                    >
                                        <FaVolumeUp />
                                        <span>Preview Voice</span>
                                    </motion.button>
                                </div>
                            </motion.div>
                        )}

                        {/* Right column: Session details */}
                        <motion.div
                            className="lg:col-span-2"
                            initial={{ opacity: 0, x: 20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.5, delay: 0.2 }}
                        >
                            <div className="bg-white p-4 sm:p-6 rounded-xl shadow-lg mb-4 sm:mb-6">
                                <div className="flex items-center mb-3 sm:mb-4">
                                    <div className="p-2 bg-blue-100 rounded-full mr-3">
                                        <FaUserTie className="text-blue-600 text-lg sm:text-xl" />
                                    </div>
                                    <h3 className="text-lg sm:text-xl font-bold text-gray-800">Session Details</h3>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-3 sm:mb-4">
                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Interview Type</span>
                                        <span className="font-semibold text-gray-800">{getInterViewLabel(type || '')}</span>
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Title</span>
                                        <span className="font-semibold text-gray-800">{interviewFormData.title}</span>
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Session Type</span>
                                        <span className="font-semibold text-gray-800">{interviewFormData.sessionType?.label}</span>
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Job Role</span>
                                        <span className="font-semibold text-gray-800">{interviewFormData.jobRole?.label}</span>
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Difficulty</span>
                                        <span className="font-semibold text-gray-800">{interviewFormData.difficulty?.label}</span>
                                    </div>

                                    <div className="flex flex-col">
                                        <span className="text-gray-500 text-sm">Duration</span>
                                        <span className="font-semibold text-gray-800">{interviewFormData.duration?.label}</span>
                                    </div>
                                </div>

                                {interviewFormData.topics && interviewFormData.topics.length > 0 && (
                                    <div className="border-t border-gray-100 pt-4 mb-4">
                                        <span className="text-gray-500 text-sm mb-2 block">Focus Points</span>
                                        <div className="flex flex-wrap gap-2">
                                            {interviewFormData.topics.map((topic, index) => (
                                                <span key={index} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                                                    {topic.label}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {selectedFiles.length > 0 && (
                                <motion.div
                                    className="bg-white p-6 rounded-xl shadow-lg mb-6"
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.3, delay: 0.3 }}
                                >
                                    <div className="flex items-center mb-4">
                                        <div className="p-2 bg-green-100 rounded-full mr-3">
                                            <FaFileUpload className="text-green-600 text-xl" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-800">Uploaded Documents</h3>
                                    </div>

                                    <div className="space-y-3">
                                        {selectedFiles.map((file, idx) => (
                                            <div key={idx} className="flex items-center p-3 bg-gray-50 rounded-lg">
                                                <FaFileUpload className="text-blue-500 mr-3" />
                                                <div>
                                                    <p className="font-medium text-gray-800">{file.name}</p>
                                                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(2)} KB</p>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </motion.div>
                    </div>

                    {/* Action buttons */}
                    <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                        <motion.button
                            onClick={() => {
                                // sanitize data for final creation
                                const topicsData =
                                    interviewFormData.topics?.map((t) => ({
                                        label: t.label,
                                        value: t.value,
                                    })) || [];

                                const jobRoleData = interviewFormData.jobRole
                                    ? {
                                        label: interviewFormData.jobRole.label,
                                        value: interviewFormData.jobRole.value,
                                    }
                                    : null;

                                const tempData: ExtendedFormInputs = {
                                    ...interviewFormData,
                                    topics: topicsData,
                                    jobRole: jobRoleData,
                                };

                                submitData(tempData);
                            }}
                            disabled={isSubmitting}
                            className="px-6 sm:px-8 py-3 sm:py-4 bg-gradient-to-r from-blue-500 to-indigo-600 text-white text-base sm:text-lg font-bold rounded-xl hover:from-blue-600 hover:to-indigo-700 shadow-lg transition flex-1 flex items-center justify-center min-h-[44px] disabled:opacity-50"
                            whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
                            whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                        >
                            {isSubmitting ? (
                                <>
                                    <FaSpinner className="animate-spin mr-2" />
                                    <span>Creating Session...</span>
                                </>
                            ) : (
                                <>
                                    <FaPlay className="mr-2" />
                                    <span>Start Interview</span>
                                </>
                            )}
                        </motion.button>

                        <motion.button
                            onClick={() => setIsReviewing(false)}
                            disabled={isSubmitting}
                            className="px-6 sm:px-8 py-3 sm:py-4 border-2 border-gray-300 text-gray-700 text-base sm:text-lg font-bold rounded-xl hover:bg-gray-100 transition flex items-center justify-center min-h-[44px] disabled:opacity-50"
                            whileHover={{ scale: isSubmitting ? 1 : 1.03 }}
                            whileTap={{ scale: isSubmitting ? 1 : 0.97 }}
                        >
                            <FaArrowLeft className="mr-2" />
                            <span>Make Changes</span>
                        </motion.button>
                    </div>

                    {/* Loading overlay for submission */}
                    {isSubmitting && (
                        <motion.div
                            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                        >
                            <motion.div
                                className="bg-white rounded-xl p-6 sm:p-8 max-w-sm w-full mx-4 shadow-2xl"
                                initial={{ scale: 0.9, opacity: 0 }}
                                animate={{ scale: 1, opacity: 1 }}
                                exit={{ scale: 0.9, opacity: 0 }}
                            >
                                <div className="flex flex-col items-center">
                                    <FaSpinner className="animate-spin text-blue-500 text-4xl mb-4" />
                                    <h3 className="text-lg font-bold text-gray-800 mb-2">Creating Your Session</h3>
                                    <p className="text-sm text-gray-600 text-center">Please wait while we set everything up for you...</p>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </motion.div>
            </div>
        );
    }

    // Initial loading skeleton
    if (isInitialLoading) {
        return (
            <div className="min-h-screen flex flex-col bg-gray-50">
                <Toast ref={toast} />
                <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8">
                    <div className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-xl w-full max-w-2xl">
                        <div className="animate-pulse space-y-6">
                            <div className="text-center mb-6">
                                <div className="h-12 w-12 bg-gray-200 rounded-full mx-auto mb-4"></div>
                                <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto mb-2"></div>
                                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto"></div>
                            </div>
                            <div className="space-y-4">
                                <div className="h-2 bg-gray-200 rounded"></div>
                                <div className="flex justify-between px-4">
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                    <div className="h-10 w-10 bg-gray-200 rounded-full"></div>
                                </div>
                            </div>
                            <div className="space-y-4">
                                <div className="h-4 bg-gray-200 rounded w-1/4"></div>
                                <div className="h-12 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col bg-gray-50">
            <Toast ref={toast} />
            <ConfirmDialog />

            {/* Exit confirmation dialog */}
            <AnimatePresence>
                {showExitConfirm && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setShowExitConfirm(false)}
                    >
                        <motion.div
                            className="bg-white rounded-xl p-6 sm:p-8 max-w-md w-full mx-4 shadow-2xl"
                            initial={{ scale: 0.9, opacity: 0 }}
                            animate={{ scale: 1, opacity: 1 }}
                            exit={{ scale: 0.9, opacity: 0 }}
                            onClick={(e) => e.stopPropagation()}
                        >
                            <h3 className="text-xl font-bold text-gray-800 mb-3">Leave Form?</h3>
                            <p className="text-gray-600 mb-6">
                                You have unsaved changes. Are you sure you want to leave? Your progress will be lost.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-end">
                                <button
                                    onClick={() => setShowExitConfirm(false)}
                                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 transition font-medium"
                                >
                                    Continue Editing
                                </button>
                                <button
                                    onClick={() => {
                                        setShowExitConfirm(false);
                                        navigate(-1);
                                    }}
                                    className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition font-medium"
                                >
                                    Leave Without Saving
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Keyboard shortcuts hint */}
            {!isReviewing && (
                <div className="fixed bottom-4 right-4 bg-white rounded-lg shadow-lg p-3 border border-gray-200 text-xs text-gray-600 max-w-xs z-40 hidden sm:block">
                    <div className="font-semibold mb-2">Keyboard Shortcuts:</div>
                    <div className="space-y-1">
                        <div><kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Ctrl/⌘</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">←</kbd> Previous step</div>
                        <div><kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Ctrl/⌘</kbd> + <kbd className="px-1.5 py-0.5 bg-gray-100 rounded">→</kbd> Next step</div>
                        <div><kbd className="px-1.5 py-0.5 bg-gray-100 rounded">Esc</kbd> Go back / Exit</div>
                    </div>
                </div>
            )}

            <div className="min-h-screen flex items-center justify-center p-3 sm:p-4 py-4 sm:py-8">
                <motion.div
                    className="bg-white p-4 sm:p-6 md:p-8 rounded-xl shadow-xl w-full max-w-2xl"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5 }}
                >
                    <motion.div
                        className="text-center mb-4 sm:mb-6"
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        transition={{ duration: 0.5 }}
                    >
                        <div className="text-3xl sm:text-4xl mb-3 sm:mb-4">{icon}</div>
                        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold">Start a New Session</h2>
                        <p className="text-sm sm:text-base text-gray-600 mt-1">
                            Configure your interview practice session
                        </p>
                    </motion.div>

                    {renderStepIndicator()}

                    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                        <AnimatePresence mode="wait">
                            {currentStep === 1 && (
                                <motion.div
                                    key="step1"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <div>
                                        <label
                                            htmlFor="title-input"
                                            className="block text-gray-700 mb-2 text-sm sm:text-base font-medium"
                                        >
                                            Session Title <span className="text-red-500">*</span>
                                        </label>
                                        <div className="relative">
                                            <input
                                                id="title-input"
                                                {...register('title', {
                                                    required: 'Session title is required',
                                                    minLength: {
                                                        value: 3,
                                                        message: 'Title must be at least 3 characters',
                                                    },
                                                    maxLength: {
                                                        value: 100,
                                                        message: 'Title must be less than 100 characters',
                                                    },
                                                })}
                                                onBlur={() => trigger('title')}
                                                maxLength={100}
                                                className={`w-full px-4 py-3 ${watch('title')?.length > 0 ? 'pr-16' : 'pr-10'} border-2 rounded-lg focus:outline-none focus:ring-2 transition-colors text-sm sm:text-base ${errors.title
                                                    ? 'border-red-500 focus:ring-red-500 focus:border-red-500'
                                                    : touchedFields.title && !errors.title
                                                        ? 'border-green-500 focus:ring-green-500 focus:border-green-500'
                                                        : 'border-gray-300 focus:ring-blue-500 focus:border-blue-500'
                                                    }`}
                                                placeholder="Enter a title describing your interview"
                                                aria-invalid={errors.title ? 'true' : 'false'}
                                                aria-describedby={errors.title ? 'title-error' : 'title-help'}
                                            />
                                            {watch('title')?.length > 0 && (
                                                <div className="absolute right-10 top-1/2 transform -translate-y-1/2 text-xs text-gray-400 font-medium">
                                                    {watch('title')?.length || 0}/100
                                                </div>
                                            )}
                                            {touchedFields.title && !errors.title && (
                                                <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                                                    <FaCheck className="text-green-500 text-lg" />
                                                </div>
                                            )}
                                        </div>
                                        {errors.title && (
                                            <p id="title-error" className="text-red-500 text-sm mt-1.5 flex items-center" role="alert">
                                                <span className="mr-1">⚠️</span>
                                                {errors.title.message}
                                            </p>
                                        )}
                                        {!errors.title && touchedFields.title && (
                                            <p id="title-help" className="text-green-600 text-xs mt-1.5">
                                                ✓ Looks good!
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Session Type <span className="text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="sessionType"
                                            control={control}
                                            rules={{ required: 'Session Type is required' }}
                                            render={({ field }) => (
                                                <div>
                                                    <Select
                                                        {...field}
                                                        inputId="session-type-select"
                                                        options={sessionTypeOptions}
                                                        placeholder="Select Session Type"
                                                        classNamePrefix="react-select"
                                                        onChange={(value) => {
                                                            field.onChange(value);
                                                            trigger('sessionType');
                                                        }}
                                                        onBlur={() => trigger('sessionType')}
                                                        aria-label="Session Type"
                                                        aria-invalid={errors.sessionType ? 'true' : 'false'}
                                                        aria-describedby={errors.sessionType ? 'session-type-error' : undefined}
                                                        styles={{
                                                            control: (base, state) => ({
                                                                ...base,
                                                                borderColor: errors.sessionType
                                                                    ? '#ef4444'
                                                                    : touchedFields.sessionType && !errors.sessionType
                                                                        ? '#10b981'
                                                                        : state.isFocused ? '#3b82f6' : '#d1d5db',
                                                                borderWidth: '2px',
                                                                boxShadow: state.isFocused
                                                                    ? errors.sessionType
                                                                        ? '0 0 0 3px rgba(239, 68, 68, 0.1)'
                                                                        : touchedFields.sessionType && !errors.sessionType
                                                                            ? '0 0 0 3px rgba(16, 185, 129, 0.1)'
                                                                            : '0 0 0 3px rgba(59, 130, 246, 0.1)'
                                                                    : 'none',
                                                                '&:hover': {
                                                                    borderColor: errors.sessionType ? '#ef4444' : '#3b82f6',
                                                                },
                                                            }),
                                                        }}
                                                    />
                                                    {touchedFields.sessionType && !errors.sessionType && field.value && (
                                                        <div className="mt-2 text-green-600 text-xs">
                                                            ✓ Selected
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                        {errors.sessionType && (
                                            <p id="session-type-error" className="text-red-500 text-sm mt-1.5 flex items-center" role="alert">
                                                <span className="mr-1">⚠️</span>
                                                {errors.sessionType.message}
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 2 && (
                                <motion.div
                                    key="step2"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Job Role <span className="text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="jobRole"
                                            control={control}
                                            rules={{ required: 'Interview Role is required' }}
                                            render={({ field }) => (
                                                <CreatableSelect
                                                    {...field}
                                                    options={jobRoleOptions}
                                                    placeholder="Select or type a job role here"
                                                    classNamePrefix="react-select"
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                        {errors.jobRole && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {errors.jobRole.message}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Difficulty Level <span className="text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="difficulty"
                                            control={control}
                                            rules={{ required: 'Difficulty is required' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={difficultyOptions}
                                                    placeholder="Select Difficulty Level"
                                                    classNamePrefix="react-select"
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                        {errors.difficulty && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {errors.difficulty.message}
                                            </p>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 3 && (
                                <motion.div
                                    key="step3"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Session Duration <span className="text-red-500">*</span>
                                        </label>
                                        <Controller
                                            name="duration"
                                            control={control}
                                            rules={{ required: 'Session Duration is required' }}
                                            render={({ field }) => (
                                                <Select
                                                    {...field}
                                                    options={populateDurationOption()}
                                                    placeholder="Select Duration"
                                                    classNamePrefix="react-select"
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                        {errors.duration && (
                                            <p className="text-red-500 text-sm mt-1">
                                                {errors.duration.message}
                                            </p>
                                        )}
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Focus Points (Optional)
                                        </label>
                                        <Controller
                                            name="topics"
                                            control={control}
                                            render={({ field }) => (
                                                <CreatableSelect
                                                    {...field}
                                                    isMulti
                                                    options={topicOptions}
                                                    placeholder="Select or enter Topics"
                                                    classNamePrefix="react-select"
                                                    onChange={(value) => field.onChange(value)}
                                                />
                                            )}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-gray-700 mb-1 text-sm font-medium">
                                            Document Sources (Optional)
                                        </label>
                                        <div
                                            {...getRootProps()}
                                            className={`border-2 border-dashed p-6 rounded-lg text-center cursor-pointer ${isDragActive ? 'border-blue-400 bg-blue-50' : 'border-gray-300'
                                                }`}
                                        >
                                            <input {...getInputProps()} />
                                            {isDragActive ? (
                                                <p className="text-blue-500">Drop the files here ...</p>
                                            ) : (
                                                <p className="text-gray-500">
                                                    Drag & drop files here, or click to select files (max 2MB)
                                                </p>
                                            )}
                                        </div>

                                        {selectedFiles.length > 0 && (
                                            <div className="mt-4 space-y-3">
                                                {selectedFiles.map((file, index) => {
                                                    const fileSizeMB = file.size / (1024 * 1024);
                                                    const fileSizeKB = file.size / 1024;
                                                    const fileSizeDisplay = fileSizeMB >= 1
                                                        ? `${fileSizeMB.toFixed(2)} MB`
                                                        : `${fileSizeKB.toFixed(2)} KB`;
                                                    const fileExtension = file.name.split('.').pop()?.toLowerCase();
                                                    const isPDF = fileExtension === 'pdf';

                                                    return (
                                                        <div
                                                            key={file.name + index}
                                                            className="flex items-center justify-between border-2 border-gray-200 p-4 rounded-lg bg-gradient-to-r from-gray-50 to-white hover:border-blue-300 transition-colors"
                                                        >
                                                            <div className="flex items-center flex-1 min-w-0">
                                                                <div className={`p-2.5 rounded-lg mr-3 flex-shrink-0 ${isPDF ? 'bg-red-100 text-red-600' : 'bg-blue-100 text-blue-600'
                                                                    }`}>
                                                                    <FaFileUpload className="text-lg" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <p className="font-medium text-sm sm:text-base truncate" title={file.name}>{file.name}</p>
                                                                    <div className="flex items-center gap-2 mt-1">
                                                                        <p className="text-xs text-gray-500">
                                                                            {fileSizeDisplay}
                                                                        </p>
                                                                        <span className="text-gray-300">•</span>
                                                                        <span className="text-xs text-gray-500 uppercase">
                                                                            {fileExtension || 'Unknown'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => removeFile(index)}
                                                                className="ml-3 text-red-600 hover:text-red-800 hover:bg-red-50 p-2 rounded-lg transition-colors flex items-center justify-center min-h-[44px] min-w-[44px]"
                                                                aria-label={`Remove ${file.name}`}
                                                            >
                                                                <FaTrash className="text-sm" />
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 4 && (
                                <motion.div
                                    key="step4"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <div>
                                        <label className="block text-gray-700 mb-3 text-sm font-medium">
                                            Choose Your Interviewer <span className="text-red-500">*</span>
                                        </label>

                                        {!isPremiumPlan(user) && (
                                            <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-4 text-sm rounded">
                                                <div className="flex">
                                                    <div className="flex-shrink-0">
                                                        <svg className="h-5 w-5 text-blue-500" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.857-9.809a.75.75 0 00-1.214-.882l-3.483 4.79-1.88-1.88a.75.75 0 10-1.06 1.061l2.5 2.5a.75.75 0 001.137-.089l4-5.5z" clipRule="evenodd" />
                                                        </svg>
                                                    </div>
                                                    <div className="ml-3">
                                                        <h3 className="font-medium text-blue-800">Interviewer pre-selected</h3>
                                                        <div className="mt-1 text-blue-700">
                                                            <p>Interviewer customization is a premium feature. Upgrade to Pro or Enterprise to choose from our full range of interviewers.</p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                                            {INTERVIEWER_PROFILES.map((interviewer) => {
                                                // For non-premium users, only show the first interviewer
                                                const isAvailable = isPremiumPlan(user) || interviewer.id === INTERVIEWER_PROFILES[0].id;
                                                const isSelected = selectedInterviewer?.id === interviewer.id;

                                                return isAvailable && (
                                                    <motion.div
                                                        key={interviewer.id}
                                                        className={`border-2 rounded-xl overflow-hidden transition-all duration-200 ${!isPremiumPlan(user) ? 'cursor-default' : 'cursor-pointer'
                                                            } ${isSelected
                                                                ? 'border-blue-500 ring-4 ring-blue-200 shadow-lg bg-blue-50'
                                                                : 'border-gray-200 hover:border-blue-300 hover:shadow-md bg-white'
                                                            } ${!isPremiumPlan(user) && interviewer.id !== INTERVIEWER_PROFILES[0].id ? 'opacity-50' : ''}`}
                                                        onClick={() => isPremiumPlan(user) && setSelectedInterviewer(interviewer)}
                                                        whileHover={isPremiumPlan(user) ? { scale: 1.02, y: -2 } : {}}
                                                        whileTap={isPremiumPlan(user) ? { scale: 0.98 } : {}}
                                                        role="button"
                                                        aria-pressed={isSelected}
                                                        aria-label={`Select ${interviewer.name} as interviewer`}
                                                        tabIndex={isPremiumPlan(user) ? 0 : -1}
                                                    >
                                                        <div className="relative">
                                                            <img
                                                                src={interviewer.image}
                                                                alt={interviewer.name}
                                                                className="w-full h-56 sm:h-48 object-cover"
                                                            />
                                                            {isSelected && (
                                                                <div className="absolute top-3 right-3 bg-blue-500 text-white p-2 rounded-full shadow-lg">
                                                                    <FaCheck className="text-sm" />
                                                                </div>
                                                            )}
                                                            <div
                                                                className={`absolute top-3 left-3 p-2 rounded-full bg-opacity-90 backdrop-blur-sm ${isSelected
                                                                    ? 'bg-blue-500 text-white'
                                                                    : 'bg-white text-gray-700 shadow-md'
                                                                    }`}
                                                            >
                                                                <FaUserTie className="text-sm" />
                                                            </div>
                                                            {!isPremiumPlan(user) && interviewer.id !== INTERVIEWER_PROFILES[0].id && (
                                                                <div className="absolute inset-0 bg-gray-800 bg-opacity-60 flex items-center justify-center">
                                                                    <div className="bg-white text-gray-800 px-4 py-2 rounded-full text-xs font-bold shadow-lg">
                                                                        Premium Only
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                        <div className="p-4 sm:p-5">
                                                            <h3 className="font-bold text-lg sm:text-xl mb-1">{interviewer.name}</h3>
                                                            <p className="text-gray-600 text-sm sm:text-base mb-2">{interviewer.title}</p>
                                                            <p className="text-gray-500 text-xs sm:text-sm mb-4 line-clamp-2">{interviewer.description}</p>

                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    playVoicePreview(interviewer.id);
                                                                }}
                                                                className={`w-full flex items-center justify-center text-sm sm:text-base px-4 py-2.5 rounded-lg font-medium transition-colors min-h-[44px] ${playingPreview === interviewer.id
                                                                    ? 'bg-green-500 text-white hover:bg-green-600'
                                                                    : isSelected
                                                                        ? 'bg-blue-500 text-white hover:bg-blue-600'
                                                                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                                                                    }`}
                                                                aria-label={`Preview voice of ${interviewer.name}`}
                                                            >
                                                                {playingPreview === interviewer.id ? (
                                                                    <>
                                                                        <FaStop className="mr-2" /> Stop Preview
                                                                    </>
                                                                ) : (
                                                                    <>
                                                                        <FaVolumeUp className="mr-2 text-lg" /> Preview Voice
                                                                    </>
                                                                )}
                                                            </button>
                                                        </div>
                                                    </motion.div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </motion.div>
                            )}

                            {currentStep === 5 && (
                                <motion.div
                                    key="step6"
                                    initial={{ opacity: 0, x: 20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -20 }}
                                    transition={{ duration: 0.3 }}
                                    className="space-y-6"
                                >
                                    <div className="text-center">
                                        <h3 className="text-xl font-bold mb-3">Ready to Begin</h3>
                                        <p className="text-gray-600">Your interview session is configured and ready to go.</p>
                                        <p className="text-gray-600">Review your settings and click "Start Session" when ready.</p>
                                    </div>

                                    {selectedInterviewer && (
                                        <div className="bg-gray-50 p-4 rounded-lg flex items-center">
                                            <img
                                                src={selectedInterviewer.image}
                                                alt={selectedInterviewer.name}
                                                className="w-16 h-16 rounded-full mr-4 object-cover"
                                            />
                                            <div>
                                                <h4 className="font-bold">{selectedInterviewer.name}</h4>
                                                <p className="text-gray-600 text-sm">{selectedInterviewer.title}</p>
                                                <button
                                                    type="button"
                                                    onClick={() => playVoicePreview(selectedInterviewer.id)}
                                                    className={`mt-2 flex items-center text-xs px-2 py-1 rounded-full ${playingPreview === selectedInterviewer.id
                                                        ? 'bg-green-500 text-white'
                                                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                                        }`}
                                                >
                                                    {playingPreview === selectedInterviewer.id ? (
                                                        <>
                                                            <FaStop className="mr-1" /> Stop
                                                        </>
                                                    ) : (
                                                        <>
                                                            <FaVolumeUp className="mr-1" /> Hear Voice
                                                        </>
                                                    )}
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                </motion.div>
                            )}
                        </AnimatePresence>

                        <div className="flex flex-col sm:flex-row justify-between gap-3 sm:gap-0 pt-4 sm:pt-6 border-t">
                            {currentStep > 1 && (
                                <button
                                    type="button"
                                    onClick={prevStep}
                                    className="flex items-center justify-center min-h-[44px] px-4 sm:px-6 py-2.5 sm:py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition font-medium text-sm sm:text-base"
                                >
                                    <FaArrowLeft className="mr-2" />
                                    Back
                                </button>
                            )}
                            {currentStep < 5 ? (
                                <button
                                    type="button"
                                    onClick={nextStep}
                                    className={`flex items-center justify-center min-h-[44px] px-6 sm:px-8 py-2.5 sm:py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition font-medium text-sm sm:text-base ${currentStep > 1 ? 'sm:ml-auto' : 'w-full sm:w-auto'}`}
                                >
                                    Next
                                    <FaArrowRight className="ml-2" />
                                </button>
                            ) : (
                                <button
                                    type="submit"
                                    className={`flex items-center justify-center min-h-[44px] px-6 sm:px-8 py-2.5 sm:py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition font-medium text-sm sm:text-base ${currentStep > 1 ? 'sm:ml-auto' : 'w-full sm:w-auto'}`}
                                >
                                    <FaPlay className="mr-2" />
                                    Start Session
                                </button>
                            )}
                        </div>
                    </form>
                </motion.div>
            </div>
        </div>
    );
};

export default NewInterviewSessionForm;
